import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  applyScopeOverride,
  readAudit,
  readIct,
  recordIctGate,
  revertScopeOverride,
  setAgreedDuration,
  setCompensation,
  withdrawAdjustment,
  type AppliedAdjustment,
  type ApprovalOutcome,
  type AuditDetail,
  type Determination,
  type IctStanding,
  type ScopeSection,
  type TrailEntry,
} from "../api/client";
import type { Role } from "../api/client";
import { ApprovalCard } from "../audit/ApprovalCard";
import { IctGatesCard, SCOPE_CONFIRMATION_GATES } from "../audit/IctGates";
import { reach, type StepKey } from "../audit/steps";
import { Callout } from "../components/Callout";
import { CheckIcon } from "../components/Icons";
import { TrailButton, TrailDrawer } from "../components/TrailDrawer";

/**
 * Screens 30 to 33 — the determination, and the departures from it. Both of screens 31 and
 * 32's dialogs are gone now; every departure here posts directly. See `ScopeSplit` and
 * `AgreedDuration`.
 *
 * **The derivation is the product.** A single figure is not auditable, so the duration card
 * is a ledger of every stage: the table and why that table, the complexity and whose it
 * was, the band, base days, each Cl. 8 factor with its justification, the reduction against
 * the Cl. 3.9 ceiling, the adjusted days, the Cl. 2.1.2 floor, the Stage 1 share in force
 * and the result.
 *
 * **Two limits, and they are not the same limit.** A Cl. 8 factor may not reduce
 * base-table time by more than 30% — a `shall`, refused. A certification body's adjustment
 * sits outside that ceiling and is caught by the 80% floor — a `should`, warned about and
 * permitted. Screen 33 is the second one happening, and the red callout says so without
 * offering a way to undo it.
 *
 * **The scope split is the one card here both parties write.** Everything about the
 * duration is the certification body's; a departure on a clause group is either party's,
 * and each reverts its own. See `ScopeSplit`.
 *
 * **The card has two columns and they answer to different authorities.** The left is the
 * *calculated audit time* — IAF MD 5's, in days, through the Cl. 3.9 ceiling and Cl. 2.2.3's
 * half-day rounding, and nothing on this screen moves it. The right is the *Stage 1
 * agreement* — certification body procedure, in hours, because MD 5 gives no formula for the
 * split at all (ADR 0001) and a remote Stage 1 session is agreed in hours. M32.
 *
 * **What is not ported.** The prototype's duration card subtracts hours from a 32.0-hour
 * Stage 1 base and prints 22.3 h, then labels it "80% of the calculated base" when it is
 * 70%. Both halves of that are still refused: the percentage is the server's, and the
 * *total* audit duration still lands on a half-day, derived back from the agreement at the
 * agreed ratio and never fed into it.
 */
export function ScopeAndDuration({
  auditId,
  role,
  onChanged,
  onStep,
}: {
  auditId: string;
  /**
   * Which party is looking. Step 2 has two readers and the writing is split by card.
   *
   * **The duration ledger has one writer.** Cl. 8 factors and duration adjustments are
   * `Role.CB` on the server, and the lead auditor approves the figures rather than editing
   * the figures they are approving — which is what makes the dual approval two decisions.
   *
   * **The scope split has two.** Both roles are admitted to the override routes, and each
   * reverts only what its own party recorded. *This clause cannot be satisfied from a
   * screen* is a judgement about audit technique, which belongs to the auditor who signs
   * the finding it produces; before this their only lever was withholding an approval, and
   * a lever whose whole vocabulary is *no* cannot name a clause.
   *
   * `writable` below is the ledger's, not the screen's. Rendering a control the server
   * refuses is what this screen used to do for the whole of Step 2 — an offered control
   * that 403s teaches nothing about who owns the decision — so where the two parties differ
   * the difference is drawn, and where they do not, both get the control.
   */
  role: Role;
  /**
   * The audit's state moved. An approval recorded here is the last one, or an override
   * changed the determination — either way the shell's step bar is drawn from a state this
   * screen has just changed, and it has to be told.
   */
  onChanged: () => void;
  /**
   * Take the reader to another stop. Used for one move: the approval that releases the
   * plan carries the lead auditor to Step 3, which is the next thing they have to do and
   * the only step this one hands work to.
   */
  onStep: (step: StepKey) => void;
}) {
  const writable = role === "cb";
  const [audit, setAudit] = useState<AuditDetail | null>(null);
  const [ict, setIct] = useState<IctStanding | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * The trail is a panel now, and it opens closed — see `TrailDrawer`. This screen is the
   * one that writes to it most, so the control sits beside the audit's own identifiers in
   * the masthead rather than at the foot of a ledger the reader has to pass first.
   */
  const [trailOpen, setTrailOpen] = useState(false);

  useEffect(() => {
    readAudit(auditId)
      .then(setAudit)
      .catch((cause: unknown) => setError(problem(cause, "The audit")));
    readIct(auditId)
      .then(setIct)
      .catch(() => undefined);
  }, [auditId]);

  if (error && !audit) {
    return (
      <p className="problem" role="alert">
        {error}
      </p>
    );
  }
  if (!audit) return <p className="sub">Reading the determination&hellip;</p>;

  const determination = audit.determination;

  const act = async (work: Promise<AuditDetail>) => {
    setError(null);
    try {
      setAudit(await work);
      onChanged();
    } catch (cause) {
      setError(problem(cause, "The change"));
    }
  };

  /**
   * The approval is in. Whether it also released the plan decides where the approver goes.
   *
   * **The move is onto work that now exists.** `plan_generated` is the server saying this
   * was the second approval and the plan was generated from it — so the lead auditor, whose
   * plan it is, is taken straight to Step 3 rather than left on a determination that is
   * finished with. An approval that is the *first* of the two generates nothing, and the
   * card's pending pill is the honest answer to it.
   *
   * **Who is moved is `reach`'s answer, not a role test written again here.** Step 3 is
   * writable for the lead auditor and read-only for the certification body — a body
   * approving second stays on the determination with the "both approvals recorded" callout,
   * because being thrown to a screen it may only read would read as work it owes.
   *
   * Nothing is re-read on the way out: the shell refreshes the audit before it swaps the
   * screen, and this one is unmounted by the swap.
   */
  const approved = async (outcome: ApprovalOutcome) => {
    const onward = reach("plan", outcome.state, role);
    if (outcome.plan_generated && onward.reachable && onward.writable) {
      onStep("plan");
      return;
    }
    await act(readAudit(auditId));
  };

  /**
   * Both parties may record. The server admits `cb` and `lead_auditor` to the gate
   * endpoint, and it is right to: the auditor approving on screen 35 is refused by the same
   * gate the certification body is, and a control rendered only to the writer of this
   * screen would leave them with a refusal and no way to answer it. This is not the
   * `writable` distinction — that one guards the figures, which are the CB's alone.
   */
  const recordGate = async (gate: string, satisfied: boolean) => {
    setError(null);
    try {
      setIct(await recordIctGate(auditId, gate, satisfied));
      setAudit(await readAudit(auditId));
    } catch (cause) {
      setError(problem(cause, "The gate"));
    }
  };

  return (
    <>
      <div className="between" style={{ marginBottom: "14px" }}>
        <div>
          <div className="eyebrow">Step 2 · Scope determination</div>
          <h1>{audit.client_organisation}</h1>
          <p className="sub">
            <span className="mono">{audit.reference}</span> ·{" "}
            {audit.standards.join(" · ")} · Stage 1 remote · {audit.planned_date}
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <span className="pill p-wait">{stateLabel(audit.state)}</span>
          <div style={{ marginTop: "6px" }}>
            <Approval party="CB" at={audit.cb_approved_at ?? null} />{" "}
            <Approval party="LA" at={audit.lead_auditor_approved_at ?? null} />
          </div>
          <div style={{ marginTop: "8px" }}>
            <TrailButton count={audit.trail.length} onOpen={() => setTrailOpen(true)} />
          </div>
        </div>
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {/*
        Screen 48. Step 6 is a condition rather than a stop on the rail, so this is where it
        appears: the determination screen, with the reason above it and both approvals back
        to pending. What moved is on the trail rather than restated here — the
        `audit.reconfirmation_required` entry carries the figures before and after.
      */}
      {audit.state === "reconfirmation" ? (
        <Callout tone="block" title="Step 6 — re-confirmation required.">
          The intake was revised, so the scope determination and duration were recalculated
          and Steps 2 to 5 repeat. Both approvals were withdrawn: each was recorded against
          figures that no longer describe this audit. Nothing out of date reaches audit day.
        </Callout>
      ) : null}

      {audit.consultancy_declared ? (
        <Callout tone="warn" title="Consultancy declared for this client.">
          ISO/IEC 17021-1 Cl. 4.2 restricts certification where the body has provided
          management system consultancy. Recorded on the audit trail and flagged for
          impartiality review.
        </Callout>
      ) : null}

      {determination === null ? (
        <Callout tone="block" title="No determination.">
          This audit carries no duration calculation. Nothing on this screen can be derived
          from an intake that was never run.
        </Callout>
      ) : (
        <>
          <Summary audit={audit} determination={determination} />
          <ScopeSplit
            sections={determination.sections}
            role={role}
            onOverride={(key) => void act(applyScopeOverride(auditId, key))}
            onRevert={(key) => void act(revertScopeOverride(auditId, key))}
          />
          <DurationCard
            determination={determination}
            writable={writable}
            onSetDuration={(hours) => void act(setAgreedDuration(auditId, hours))}
            onSetCompensation={(pct) => void act(setCompensation(auditId, pct))}
            onWithdraw={(id) => void act(withdrawAdjustment(auditId, id))}
          />
          {/*
            The two gates Cl. 4.2.2 and Cl. 4.2.7 place before scope confirmation, on the
            screen they block. They used to be recordable only from pre-audit readiness,
            which opens after both approvals — so the gate refused the approval and the
            approval was the only way to the gate. The refusal names the route out, and the
            route out has to be reachable from where the refusal is read.
          */}
          {ict ? (
            <IctGatesCard
              standing={ict}
              step="scope_confirmation"
              only={SCOPE_CONFIRMATION_GATES}
              onRecord={(gate, ok) => void recordGate(gate, ok)}
            />
          ) : null}
          <ApprovalCard
            auditId={auditId}
            approvals={audit.approvals ?? []}
            determination={determination}
            onApproved={(outcome) => void approved(outcome)}
          />
          {/*
            The button row that used to sit here is gone. It was navigation to Steps 3, 4–5
            and the report, gated on the same states the step bar now reads, and keeping
            both would be two navigations over one state machine — which drift.

            The trail card that sat below it is gone too, into the drawer at the masthead.
            It was the longest block on the screen and the furthest from the two dialogs
            that write to it.
          */}
        </>
      )}

      <TrailDrawer
        open={trailOpen}
        onClose={() => setTrailOpen(false)}
        entries={audit.trail}
        describe={sentence}
        marked={(entry) => DEPARTURES.has(entry.event_type)}
      />
    </>
  );
}

/** The four figures the prototype puts above the scope split. */
function Summary({
  audit,
  determination,
}: {
  audit: AuditDetail;
  determination: Determination;
}) {
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: "6px", flexWrap: "wrap", gap: "24px" }}>
        <div>
          <div className="eyebrow">Standards</div>
          {audit.standards.join(" · ")}
        </div>
        <div>
          <div className="eyebrow">Effective personnel</div>
          <span className="mono">{determination.effective_personnel}</span> · band{" "}
          <span className="mono">{determination.band}</span>
        </div>
        <div>
          <div className="eyebrow">Sites</div>
          {audit.site_count}
        </div>
        <div>
          <div className="eyebrow">Complexity</div>
          {determination.complexity === null ? (
            <span className="sub">Not specified — no OH&S risk sector was given at intake</span>
          ) : (
            <>
              {determination.complexity.applied}
              {determination.complexity.source === "lowered_by_cb" ? (
                <span className="sub">
                  {" "}
                  — lowered from {determination.complexity.sector_default}
                </span>
              ) : (
                <span className="sub"> — sector default</span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="sub" style={{ fontSize: "11.5px" }}>
        {determination.personnel_basis}
      </div>
    </div>
  );
}

/**
 * Screen 30's "Scope split by clause".
 *
 * The sub-ranges are resolved per standard and unioned to the widest, so an ISO 45001 audit
 * shows 8.1 – 8.2 rather than ISO 9001's 8.1 – 8.7. A row holding a checkpoint that cannot
 * be satisfied remotely carries the Cl. 4.5 constraint here — on the determination, before
 * the session, rather than at sign-off.
 *
 * **Both parties write here, and this is the one card on the screen where that is true.**
 * The duration figures below stay the certification body's: a body that could not be
 * disagreed with on hours would make the dual approval one decision. Scope is the other
 * kind of judgement — *this clause cannot be satisfied from a screen* is a statement about
 * audit technique, and the lead auditor is the person who owns audit technique and signs
 * the finding it produces. Withholding an approval was their only lever, and a lever whose
 * whole vocabulary is *no* cannot say **which** clause needs presence.
 *
 * **A party reverts its own departure and not the other's.** The server refuses the other
 * way round and this draws what the server will do: a row carrying somebody else's override
 * reads back who recorded it and offers nothing. The disagreement has a route already — the
 * approval, which every override withdraws.
 *
 * **Override posts directly; there is no dialog.** Decided 1 September 2026 — see
 * `apply_scope_override`'s composed reason. It still only ever records auditor presence,
 * never a request for "remote" — there is no such request shape. ⚠ **On a Cl. 4.5 row this
 * presence is also what the grading gate (`remote_sign_off_refusal`) needs to stop refusing
 * a finding**, decided 8 September 2026: the row reads "onsite" until this is recorded and
 * "remote" once it is, naming that the checkpoint can now be carried to a finding in this
 * session — Revert puts it straight back. The grading gate itself is untouched by this; it
 * reads `remote_blocked` and the same table directly, not this pill.
 */
function ScopeSplit({
  sections,
  role,
  onOverride,
  onRevert,
}: {
  sections: ScopeSection[];
  /**
   * Which party is looking, and therefore which rows carry a *Revert*. Matched against
   * `override_role` — the role of whoever applied it — rather than against a person,
   * because a certification body has more than one member of staff and the rule is that
   * the body reverts what the body recorded.
   */
  role: Role;
  onOverride: (clauseGroup: string) => void;
  onRevert: (clauseGroup: string) => void;
}) {
  const overridden = sections.filter((section) => section.override_reason);

  return (
    <div className="card">
      <h2>Scope split by clause</h2>
      <div className="tscroll">
        <table>
          <thead>
            <tr>
              <th>Clause</th>
              <th>Requirement group</th>
              <th>Delivery</th>
              <th>Method and basis</th>
              {/* The action column is drawn for both parties now, so it is unconditional. */}
              <th />
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => (
              <tr key={section.key}>
                <td>
                  <span className="mono">Cl. {section.key}</span>
                </td>
                <td>
                  <b>{section.title}</b>
                  <br />
                  <span className="sub">
                    {section.sub_range} · {section.checkpoints} checkpoint
                    {section.checkpoints === 1 ? "" : "s"}
                  </span>
                </td>
                <td>
                  <Delivery value={section.delivery} />
                </td>
                <td className="sub">
                  {section.method}
                  <br />
                  {section.basis}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {!section.override_reason ? (
                    <button
                      type="button"
                      className="btn sec sm"
                      onClick={() => onOverride(section.key)}
                    >
                      Override
                    </button>
                  ) : section.override_role === role ? (
                    <button
                      type="button"
                      className="btn sec sm"
                      onClick={() => onRevert(section.key)}
                    >
                      Revert
                    </button>
                  ) : (
                    // Not a disabled button. A control that refuses teaches nothing about
                    // who owns the decision; the sentence says whose departure it is, and
                    // the approval is the answer to disagreeing with it.
                    <span className="sub">By {party(section.override_role)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {overridden.length ? (
        <>
          <div className="hr" />
          <div className="eyebrow">Overrides applied</div>
          {overridden.map((section) => (
            <div className="plan-ed" key={section.key} style={{ marginBottom: "8px" }}>
              <b className="mono">Cl. {section.key}</b> → auditor present.
              <div style={{ marginTop: "4px" }}>{section.override_reason}</div>
              <div className="edited-tag" style={{ marginTop: "5px" }}>
                {section.override_by ?? "—"} · {party(section.override_role)} ·{" "}
                {stamp(section.override_at)}
              </div>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}

/** Exactly two states — "remote" or "onsite". See `read_scope` for how the two collapse. */
function Delivery({ value }: { value: string }) {
  if (value === "onsite") {
    return <span className="pill p-wait">On-site</span>;
  }
  return <span className="pill p-ok">Complao — remote</span>;
}

/**
 * The derivation, and the departures from it.
 *
 * Left: what the engine produced, stage by stage. Right: what the certification body
 * agreed, derived from the adjustment rows rather than stored as a total — "rows, never a
 * computed total" is the data dictionary's phrase and the reason `duration_adjustments`
 * has no summary column.
 */
function DurationCard({
  determination,
  writable,
  onSetDuration,
  onSetCompensation,
  onWithdraw,
}: {
  determination: Determination;
  /** An adjustment is this certification body's departure. Only it may record one. */
  writable: boolean;
  /** A new agreed Stage 1 duration, in hours. See `AgreedDuration`. */
  onSetDuration: (hours: string) => void;
  /** A new compensation, as a signed percentage. See `Compensation`. */
  onSetCompensation: (percent: string) => void;
  onWithdraw: (adjustmentId: string) => void;
}) {
  const ceiling = useMemo(
    () => percent(determination.reduction_ceiling),
    [determination.reduction_ceiling],
  );

  /**
   * Everything except the compensation, which has its own line.
   *
   * The compensation is a `duration_adjustments` row like any other and would otherwise be
   * drawn twice — once as a ledger entry with a *Withdraw* link and once as the field
   * holding it. Two controls over one row is how a reader comes to withdraw a compensation
   * the field then still shows. Setting the field to 0 is the withdrawal.
   */
  const ledgerRows = useMemo(
    () => determination.adjustments.filter((entry) => entry.type !== "compensation"),
    [determination.adjustments],
  );

  /**
   * What the compensation costs, in the unit the total is in.
   *
   * Read off the row rather than multiplied out here. The server decides what a percentage
   * of the calculated allocation comes to and the ledger is derived from rows — a screen
   * that computed it a second way would be a second answer, and the two would part company
   * the first time the basis moved.
   */
  const compensationHours = useMemo(
    () => determination.adjustments.find((entry) => entry.type === "compensation")?.hours,
    [determination.adjustments],
  );

  return (
    <div className="card">
      <h2>Audit duration</h2>
      <div className="grid2">
        <div>
          <div className="eyebrow">Derivation</div>
          <div className="ledger">
            <div className="base">
              <span>
                Initial audit base
                <small>
                  {determination.base_table_label}
                  {determination.complexity !== null
                    ? ` · ${determination.complexity.applied} complexity`
                    : ""}{" "}
                  · band {determination.band}
                </small>
                {determination.complexity !== null &&
                determination.complexity.source === "lowered_by_cb" ? (
                  <small>
                    Lowered from {determination.complexity.sector_default} —{" "}
                    {determination.complexity.justification}
                  </small>
                ) : null}
              </span>
              <span className="v">{days(determination.base_days)}</span>
            </div>

            {Number(determination.additional_standard_days) > 0 ? (
              <div>
                <span>
                  Additional standards
                  <small>
                    Provisional. IAF MD 5 provides no multi-standard uplift — IAF MD 11
                    governs and is not seeded (open item RD-2).
                  </small>
                </span>
                <span className="v">+{days(determination.additional_standard_days)}</span>
              </div>
            ) : null}

            {determination.factors.map((factor) => (
              <div
                key={factor.factor_id}
                className={factor.direction === "subtractive" ? "neg" : undefined}
              >
                <span>
                  {factor.label}
                  <small>{factor.justification}</small>
                  <small className="mono">
                    Cl. 8 {factor.factor_id} · {String(factor.value)}% of base-table time
                  </small>
                </span>
                <span className="v">
                  {factor.direction === "subtractive" ? "−" : "+"}
                  {days(factor.days)}
                </span>
              </div>
            ))}

            <div>
              <span>
                Reduction against the Cl. 3.9 ceiling
                <small>
                  A <i>shall</i>: total reduction may not exceed {ceiling} of base-table
                  time. Exceeding it is refused, never capped.
                </small>
              </span>
              <span className="v">
                {percent(determination.reduction_ratio)} of {ceiling}
              </span>
            </div>

            <div>
              <span>Adjusted audit time — Stage 1 + Stage 2</span>
              <span className="v">{days(determination.adjusted_days)}</span>
            </div>

            <div>
              <span>
                Duration floor
                <small>
                  IAF MD 5 Cl. 2.1.2 — a <i>should</i>, at{" "}
                  {determination.duration_threshold_percent}% of the calculated audit time.
                  A documented departure is permitted.
                </small>
              </span>
              <span className="v">{days(determination.duration_floor_days)}</span>
            </div>

            <div>
              <span>
                Stage 1 allocation
                <small>
                  {determination.stage1_percentage_applied}% of the adjusted audit time,
                  rounded to the nearest half day. Certification body procedure — IAF MD 5
                  provides no formula for the split.
                </small>
              </span>
              <span className="v">{days(determination.stage1_days)}</span>
            </div>

            <div className="base">
              <span>Stage 1 base duration</span>
              <span className="v">{hours(determination.stage1_hours)}</span>
            </div>
          </div>

          <p className="sub" style={{ marginTop: "10px", fontSize: "11px" }}>
            Engine {determination.engine_version} · MD 5 ruleset{" "}
            {determination.md5_ruleset_version} · determined {stamp(determination.calculated_at)}.
            Persisted, not recomputed — the ruleset moves.
          </p>
        </div>

        <div>
          <div className="eyebrow">Stage 1 agreement</div>
          <div className="ledger">
            <div className="base">
              <span>
                Calculated Stage 1 allocation
                <small>
                  {determination.stage1_percentage_applied}% of the adjusted audit time —{" "}
                  {days(determination.stage1_days)} × 8. What the agreement starts from.
                </small>
              </span>
              <span className="v">{hours(determination.stage1_hours)}</span>
            </div>

            {ledgerRows.map((adjustment) => (
              <Adjustment
                key={adjustment.id}
                adjustment={adjustment}
                writable={writable}
                onWithdraw={() => onWithdraw(adjustment.id)}
              />
            ))}

            <div
              className={Number(determination.compensation_percent) < 0 ? "neg" : undefined}
            >
              <span>
                Compensation
                <small>
                  A percentage deducted from the calculated Stage 1 allocation. No
                  justification is taken, and it does not add time.
                </small>
                {compensationHours === undefined ? null : (
                  <small className="mono">
                    −{hours(Math.abs(Number(compensationHours)))} against{" "}
                    {hours(determination.stage1_hours)}
                  </small>
                )}
              </span>
              <span className="v">
                {writable ? (
                  <Compensation
                    value={determination.compensation_percent}
                    onSet={onSetCompensation}
                  />
                ) : (
                  `${String(Math.abs(Number(determination.compensation_percent)))}%`
                )}
              </span>
            </div>

            <div className="tot">
              <span>
                Final agreed duration
                <small>
                  Stage 1, remote. The agenda is built to sum to exactly this.
                </small>
              </span>
              <span className="v">
                {writable ? (
                  <AgreedDuration
                    value={determination.agreed_stage1_hours}
                    onSet={onSetDuration}
                  />
                ) : (
                  hours(determination.agreed_stage1_hours)
                )}
              </span>
            </div>
          </div>

          <div className="sub" style={{ marginTop: "8px" }}>
            {String(determination.agreed_percent_of_calculated)}% of the calculated Stage 1
            allocation · Cl. 2.1.2 floor{" "}
            {hours(determination.stage1_floor_hours)} (
            {determination.duration_threshold_percent}%) · total agreed duration{" "}
            {days(determination.agreed_days)}.
          </div>
        </div>
      </div>

      {determination.below_floor ? (
        <Callout tone="block" title="Below the duration threshold.">
          {determination.threshold_message} The departure and its reasons are recorded
          prominently on the audit trail and will be visible to an accreditation body
          assessor.
        </Callout>
      ) : null}
    </div>
  );
}

/**
 * The agreed duration, set here rather than through a dialog.
 *
 * **It sets Stage 1 hours.** This is a Stage 1 platform: the figure is the length of the
 * remote session, the agenda is built to sum to exactly it (ADR 0004), and it is what the
 * approval names. The calculated audit time above — the base table, the Cl. 8 factors, the
 * Cl. 3.9 ceiling and the Cl. 2.2.3 half-day rounding — is untouched by this control; what
 * the certification body is setting is how much of that time Stage 1 gets, which is body
 * procedure, because MD 5 gives no formula for the split (ADR 0001). The total is shown
 * beneath as what follows.
 *
 * **One hour is the step**, because that is the granularity a remote session is agreed in
 * and the granularity the ledger records. Typing is still open and 25.4 becomes 25 — the
 * rounding is to the *nearest*, never up, for the same reason Cl. 2.2.3 is.
 *
 * **`type="text"` with an `inputMode`.** Never `type="number"`: it returns `null` from
 * `selectionStart` so the caret cannot be restored across a re-render, and a scroll wheel
 * over it would silently change a figure that feeds a duration determination. That is the
 * whole reason `TextField` exists, and the reason is not weaker for being inline.
 *
 * **Nothing commits on a keystroke, and a burst of presses is one commit.** Each commit is
 * an adjustment row on the record with a name and a timestamp, so typing `24` must not
 * first record `2`, and stepping from 20 to 24 must not leave four departures in the ledger
 * where the reader made one decision. Typing commits on blur or Enter; the steppers settle
 * first and commit once. Escape puts the server's figure back.
 */
function AgreedDuration({
  value,
  onSet,
}: {
  /** The agreed Stage 1 duration in hours, as the server derived it. */
  value: string;
  onSet: (hours: string) => void;
}) {
  return (
    <Stepper
      value={value}
      by={1}
      unit="hours"
      floor={1}
      decrementLabel="Reduce the agreed Stage 1 duration by one hour"
      incrementLabel="Increase the agreed Stage 1 duration by one hour"
      fieldLabel="Final agreed duration in Stage 1 hours"
      onSet={onSet}
    />
  );
}

/**
 * The certification body's time compensation — a deduction, and no reason.
 *
 * **It only ever takes time off, so it shows how much.** The field is a magnitude — `50`
 * reads as fifty per cent off — and carries no minus sign, because a sign is a direction
 * and this control has one direction. `+` deepens the deduction, `−` walks it back, and the
 * decrement stops at 0, which is the absence of a compensation; applying 0 withdraws the
 * row. Something that could also *add* audit time would be a second way to lengthen an
 * audit sitting where a body expects to shorten one — the duration field above is the way
 * to lengthen it, and it says what it is doing.
 *
 * ⚠ **The row it writes is still signed**, at `-deduction`, because the ledger sums signed
 * values. The magnitude belongs to the control and the sign belongs to the record; the
 * effect in hours beside the field carries the minus, which is where a reader looks to see
 * which way the time went.
 *
 * **It is one value, not a ledger.** A body that sets −10 and then −15 means −15, so the
 * server replaces the row rather than appending to it. That is why this is a field rather
 * than another entry in the list above, and why the row it writes is filtered out of that
 * list: two controls over one row is how a compensation comes to be withdrawn in one place
 * and still shown in the other.
 *
 * **Nothing commits until *Apply* is pressed.** Unlike the duration field beside it, moving
 * this control is a draft. The two differ because the gestures differ: a half-press of a
 * duration stepper is still a duration, and a body reads back what they set — whereas a
 * compensation is typed as a whole figure (`-5` passes through `-` and then `-5`), and each
 * commit here **withdraws the standing row and writes another**, so an intermediate value
 * committed on the way to the real one is two decisions on the trail that nobody made.
 * *Apply* is disabled until the draft is a readable figure at or below zero and different
 * from what is recorded.
 *
 * ⚠ **No justification is taken, and that is a decision.** The contract still puts
 * `minLength: 10` on `DurationAdjustment.reason` and the adjustment route still refuses a
 * short one — this control has its own route, and the server composes a sentence recording
 * the percentage and that no justification was given. Do not add a reason field here on the
 * reasoning that the row looks bare; the row says so itself.
 */
function Compensation({
  value,
  onSet,
}: {
  /**
   * The percentage currently recorded, as the *row* holds it — negative, or `0` when there
   * is no row. Turned into the magnitude the field shows here rather than by the caller, so
   * that one place knows the control's unit differs from the ledger's.
   */
  value: string;
  onSet: (deduction: string) => void;
}) {
  return (
    <Stepper
      value={String(Math.abs(Number(value)))}
      by={1}
      unit="%"
      floor={0}
      action="Apply"
      decrementLabel="Deduct one percent less"
      incrementLabel="Deduct one percent more"
      fieldLabel="Time compensation, as a percentage deducted from the calculated Stage 1 allocation"
      onSet={onSet}
    />
  );
}

/**
 * The mechanics both duration controls share — caret, settling and what a commit is.
 *
 * Written once because the discipline is the same discipline and it is not obvious: each of
 * these fields writes a `duration_adjustments` row with a name and a timestamp on it, so a
 * keystroke is not a decision and a burst of presses is one. Two copies of that would be
 * two answers to when a departure gets recorded.
 *
 * `floor` is optional and is the lowest value the decrement will offer. The duration has
 * one — an audit of no hours is not a shorter audit — and the compensation does not, since
 * it is signed on purpose.
 */
function Stepper({
  value,
  by,
  unit,
  floor,
  action,
  decrementLabel,
  incrementLabel,
  fieldLabel,
  onSet,
}: {
  value: string;
  by: number;
  unit: string;
  /**
    * Lowest value the decrement will offer, where the figure has one.
    *
    * Both controls have one and neither has an upper bound: an audit of no hours is not a
    * shorter audit, and a deduction of none is the absence of a compensation. What lies
    * above each is the server's to refuse — a deduction of 100% leaves no audit, and it is
    * caught by the same check that catches a duration set to zero.
    */
  floor?: number;
  /**
   * Present when the commit is a **button press** rather than leaving the field.
   *
   * The label goes on the button. Without it the field settles and commits itself, which is
   * right where the control is the figure — the reader moved it, so they set it. With it,
   * moving the control is a draft and nothing reaches the record until the press.
   */
  action?: string;
  decrementLabel: string;
  incrementLabel: string;
  fieldLabel: string;
  onSet: (next: string) => void;
}) {
  /**
   * The recorded figure as a person would write it.
   *
   * `Decimal` reaches the client with the scale the arithmetic gave it — a −10% of 32.0 h
   * compensation arrives as `28.800` — and a field that has to be cleared before it can be
   * typed in is a field that looks broken. The value is unchanged; only the trailing zeros
   * go, and the comparison below is numeric either way.
   */
  const settled = String(Number(value));

  const [draft, setDraft] = useState(settled);
  const input = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  const settling = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The server answered — a commit landed, or one was refused and the ledger is unchanged.
  // Either way the field shows what is actually recorded rather than what was attempted.
  // It fires on the *recorded* figure changing, so a draft nobody has applied yet survives
  // a re-render caused by something else on the screen.
  useEffect(() => setDraft(settled), [settled]);

  useEffect(() => () => {
    if (settling.current !== null) clearTimeout(settling.current);
  }, []);

  useLayoutEffect(() => {
    const field = input.current;
    if (!field || caret.current === null) return;
    const at = Math.min(caret.current, field.value.length);
    field.setSelectionRange(at, at);
    caret.current = null;
  });

  const current = Number.isFinite(Number(draft)) ? Number(draft) : Number(value);

  /** Whether this draft is a decision — readable, in range, and not what is already there. */
  const ready =
    Number.isFinite(Number(draft)) &&
    current !== Number(value) &&
    (floor === undefined || current >= floor);

  const commit = (next: string) => {
    if (settling.current !== null) clearTimeout(settling.current);
    settling.current = null;
    const parsed = Number(next);
    // Unreadable or unchanged: put the recorded figure back rather than post a departure
    // the server will refuse. Its refusals are for figures it disagrees with, not for a
    // field left as it was found.
    if (!Number.isFinite(parsed) || parsed === Number(value)) {
      setDraft(settled);
      return;
    }
    onSet(String(parsed));
  };

  const step = (delta: number) => {
    const next = Math.round(current + delta);
    if (floor !== undefined && next < floor) return;
    setDraft(String(next));
    // With a button, a press moves the draft and nothing else — the button is the commit.
    if (action !== undefined) return;
    // Without one, settle before committing, so 20 → 24 in four presses is one row rather
    // than four. The delay is the pause between presses, not a wait the reader notices at
    // the end of one — and any commit from the field itself cancels it first.
    if (settling.current !== null) clearTimeout(settling.current);
    settling.current = setTimeout(() => commit(String(next)), 700);
  };

  return (
    <span className="stepper-set">
      {/*
        One bordered surface holding both handles and the figure, divided by hairlines —
        see the note above `.stepper-set` in `app.css` for what was and was not taken from
        the supplied mockup. The unit is inside the field because `25 hours` is one value.
      */}
      <span className="stepper">
        <button
          type="button"
          onClick={() => step(-by)}
          aria-label={decrementLabel}
          disabled={floor !== undefined && current - by < floor}
        >
          −
        </button>
        <span className="stepper-rule" aria-hidden="true" />
        <span className="stepper-value">
          <input
            ref={input}
            type="text"
            inputMode="decimal"
            value={draft}
            aria-label={fieldLabel}
            onChange={(event) => {
              caret.current = event.target.selectionStart ?? event.target.value.length;
              setDraft(event.target.value);
            }}
            // A field whose commit is a button must not also commit by being left. A reader
            // who tabs away from a figure they were still deciding on has not decided.
            onBlur={action === undefined ? () => commit(draft) : undefined}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                if (action === undefined || ready) commit(draft);
              }
              if (event.key === "Escape") setDraft(settled);
            }}
          />
          <em>{unit}</em>
        </span>
        <span className="stepper-rule" aria-hidden="true" />
        <button type="button" onClick={() => step(by)} aria-label={incrementLabel}>
          +
        </button>
      </span>
      {action === undefined ? null : (
        <button
          type="button"
          className="btn sm stepper-apply"
          disabled={!ready}
          onClick={() => commit(draft)}
        >
          <CheckIcon size={12} />
          {action}
        </button>
      )}
    </span>
  );
}

/**
 * What the reader typed, in the unit they typed it in.
 *
 * `hours` and `days` are screen 32's dialog and M29's field, and both describe a departure
 * from the **calculated audit time** rather than from the Stage 1 allocation. The ledger
 * costs them into Stage 1 hours through the stamped split, so the entered figure is not
 * simply the shown one in other clothes — it is a different figure, and saying which is
 * which is the difference between a row that explains itself and one that looks wrong.
 */
function entered(adjustment: AppliedAdjustment): string {
  if (adjustment.unit === "percent") return `${String(adjustment.value)}%`;
  return `${String(adjustment.value)} ${adjustment.unit === "days" ? "d" : "h"} of the total`;
}

function Adjustment({
  adjustment,
  writable,
  onWithdraw,
}: {
  adjustment: AppliedAdjustment;
  writable: boolean;
  onWithdraw: () => void;
}) {
  const reducing = Number(adjustment.hours) < 0;
  /*
    The duration field's own rows say what they are, not that they are untyped.

    `unspecified` and the composed sentence beside it are the *record* that no type and no
    justification were taken, and both are still written — on the row, on the
    `audit.adjustment_applied` event and on the trail an assessor reads. What they are not
    is copy for the certification body's own ledger, where every row is a commit of the
    stepper directly above and "Unspecified · No justification was given" says the same
    thing about all of them. Removed from this card on 31 August 2026; do not take the
    absence here as licence to stop writing them.
  */
  const untyped = adjustment.type === "unspecified";
  return (
    <div className={reducing ? "neg" : undefined}>
      <span>
        {untyped ? "Agreed Stage 1 duration" : label(adjustment.type)}
        {untyped ? null : <small>{adjustment.reason}</small>}
        <small className="mono">
          {adjustment.applied_by ?? "—"} · {stamp(adjustment.applied_at)}
        </small>
        {writable ? (
          <button
            type="button"
            className="linkish"
            onClick={onWithdraw}
            style={{ fontSize: "11px" }}
          >
            Withdraw
          </button>
        ) : null}
      </span>
      <span className="v">
        {hours(adjustment.hours)}
        {/*
          What was entered, where that is not what is shown. A percentage, and a departure
          recorded against the *whole* audit in hours or days, all resolve to Stage 1 hours
          before they reach the ledger — so the figure the reader typed is worth keeping
          beside the one it became. A `stage1_hours` row is what the duration field writes
          and was already entered in these hours; "(4 h)" beside "4.0 h" is the same fact
          twice.
        */}
        {adjustment.unit === "stage1_hours" ? null : (
          <>
            {" "}
            <span className="sub">({entered(adjustment)})</span>
          </>
        )}
      </span>
    </div>
  );
}

/**
 * Screens 32 and 33's audit trail, which is now the drawer rather than a card.
 *
 * The list itself lives in `components/TrailDrawer`; what stays here is the part that is
 * this screen's own — which events read as a departure, and the sentence each one tells.
 *
 * Amber marks a departure — an override, an adjustment, a threshold warning. The threshold
 * warning is its own line rather than a property of the adjustment that caused it, which is
 * what "logged prominently" means: a field an assessor has to go looking for is not it.
 */

/** Events that record a departure rather than something the platform did. */
const DEPARTURES = new Set([
  "audit.scope_overridden",
  "audit.scope_override_reverted",
  "audit.adjustment_applied",
  "audit.threshold_warning",
  "audit.allocation_departure",
  "audit.complexity_lowered",
  "audit.consultancy_declared",
]);

function sentence(entry: TrailEntry): string {
  const payload = (entry.payload ?? {}) as Record<string, unknown>;
  switch (entry.event_type) {
    case "audit.intake_submitted":
      return `Intake submitted — ${String(payload.reference ?? "")}.`;
    case "audit.determination_calculated":
      return `Determination calculated — ${String(payload.base_table ?? "")}, band ${String(
        payload.band ?? "",
      )}, Stage 1 ${String(payload.stage1_hours ?? "")} h.`;
    case "audit.complexity_lowered":
      return `Complexity lowered from ${String(payload.sector_default ?? "")} to ${String(
        payload.applied ?? "",
      )}. Justification recorded.`;
    case "audit.allocation_departure":
      return `Allocation departure — ${String(payload.detail ?? "")}`;
    case "audit.consultancy_declared":
      return "Management system consultancy declared for this client. Flagged for impartiality review.";
    case "audit.factors_applied":
      return `Cl. 8 factors applied. Adjusted audit time now ${String(
        payload.adjusted_days ?? "",
      )} days.`;
    case "audit.scope_overridden":
      return `Scope override applied to Cl. ${String(
        payload.clause_group ?? "",
      )} — auditor presence required. Reason recorded.`;
    case "audit.scope_override_reverted":
      return `Scope override on Cl. ${String(payload.clause_group ?? "")} reverted.`;
    case "audit.adjustment_applied":
      return payload.withdrawn === true
        ? `Duration adjustment withdrawn — ${label(String(payload.type ?? ""))}.`
        : `Duration adjustment applied — ${label(String(payload.type ?? ""))}. Agreed Stage 1 duration now ${String(
            payload.agreed_stage1_hours ?? "",
          )} h (${String(
            payload.percent_of_calculated ?? "",
          )}% of the calculated allocation).`;
    case "audit.threshold_warning":
      return `Threshold warning raised: the agreed Stage 1 duration is ${String(
        payload.agreed_stage1_hours ?? "",
      )} h — ${String(payload.percent_of_calculated ?? "")}% of the calculated allocation, below the ${String(
        payload.threshold_percent ?? "",
      )}% floor of ${String(payload.floor_hours ?? "")} h. Permitted and recorded.`;
    default:
      return entry.event_type;
  }
}

/*
  Screen 31's override dialog stood here and is gone. Reaching *auditor present* used to
  require a reason typed into it, mandatory at ten characters because `contracts/openapi.json`
  put `minLength: 10` on `ScopeOverride.reason` — decided 1 September 2026 to drop, against
  the same advice `PUT /agreed-duration` and `PUT /compensation` were shown on 28 and 31
  August 2026. `ScopeSplit`'s *Override* button now posts directly; see
  `apply_scope_override` for the composed sentence that replaces the typed reason.

  Screen 32's adjustment dialog stood here too, and is gone for the same underlying reason
  as `AgreedDuration` above — the certification body asked on 28 August 2026 for that figure
  to be set directly. The route it posted to — `POST /audits/{id}/adjustments`, with its
  `minLength: 10` on the reason — is untouched and still refuses a short one. Nothing on this
  screen calls it.
*/

function Approval({ party, at }: { party: string; at: string | null }) {
  return at ? (
    <span className="pill p-ok">{party} approved</span>
  ) : (
    <span className="pill p-wait">{party} pending</span>
  );
}

/**
 * One party, named the way the server's refusals and the trail name it.
 *
 * A departure with no role behind it is one whose applying account has since been removed —
 * `override_by` is a left join and so is this. It reads as neither party rather than as the
 * reader's own, which is what decides whether a *Revert* is offered.
 */
function party(role: string | null | undefined): string {
  if (role === "lead_auditor") return "the lead auditor";
  if (role === "cb") return "the certification body";
  return "a withdrawn account";
}

function stateLabel(state: string): string {
  return state.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

function label(value: string): string {
  return value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** Days, always to one decimal, so 4 and 4.5 line up in the ledger. */
function days(value: string | number): string {
  return `${Number(value).toFixed(2).replace(/0$/, "")} days`;
}

function hours(value: string | number): string {
  return `${Number(value).toFixed(1)} hours`;
}

function percent(ratio: string | number): string {
  return `${(Number(ratio) * 100).toFixed(1)}%`;
}

function stamp(value: string | null | undefined): string {
  return value ? value.slice(0, 16).replace("T", " ") : "—";
}

function problem(cause: unknown, subject: string): string {
  return cause instanceof ApiError ? cause.message : `${subject} could not be read.`;
}
